/**
 * TechCheckModal - In-session equipment check dialog
 * Allows the Gutachter to verify camera, microphone, and speaker
 * without leaving the video consultation.
 */

import { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Button,
  Badge,
  saveDevicePreference,
  getPreferredDeviceId,
} from "@jourfx/ui";
import {
  Camera,
  CameraOff,
  Mic,
  MicOff,
  Volume2,
  CheckCircle2,
  XCircle,
  RefreshCw,
  ChevronDown,
} from "lucide-react";

type DeviceStatus = "pending" | "checking" | "ok" | "error";

interface DeviceCheckResult {
  status: DeviceStatus;
  message: string;
  deviceLabel?: string;
}

interface TechCheckModalProps {
  open: boolean;
  onClose: () => void;
}

export function TechCheckModal({ open, onClose }: TechCheckModalProps) {
  const { t } = useTranslation("video");

  const [cameras, setCameras] = useState<MediaDeviceInfo[]>([]);
  const [microphones, setMicrophones] = useState<MediaDeviceInfo[]>([]);
  const [speakers, setSpeakers] = useState<MediaDeviceInfo[]>([]);

  const [selectedCamera, setSelectedCamera] = useState("");
  const [selectedMic, setSelectedMic] = useState("");
  const [selectedSpeaker, setSelectedSpeaker] = useState("");

  const [cameraResult, setCameraResult] = useState<DeviceCheckResult>({
    status: "pending",
    message: t("techCheck.notChecked"),
  });
  const [micResult, setMicResult] = useState<DeviceCheckResult>({
    status: "pending",
    message: t("techCheck.notChecked"),
  });
  const [speakerResult, setSpeakerResult] = useState<DeviceCheckResult>({
    status: "pending",
    message: t("techCheck.notChecked"),
  });

  const [audioLevel, setAudioLevel] = useState(0);

  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const animFrameRef = useRef<number>(0);

  const stopStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    if (animFrameRef.current) {
      cancelAnimationFrame(animFrameRef.current);
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
  }, []);

  // Cleanup on close/unmount
  useEffect(() => {
    if (!open) {
      stopStream();
    }
    return () => stopStream();
  }, [open, stopStream]);

  const enumerateDevices = useCallback(async () => {
    try {
      const tempStream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: true,
      });
      tempStream.getTracks().forEach((t) => t.stop());

      const devices = await navigator.mediaDevices.enumerateDevices();
      const cams = devices.filter((d) => d.kind === "videoinput");
      const mics = devices.filter((d) => d.kind === "audioinput");
      const spks = devices.filter((d) => d.kind === "audiooutput");

      setCameras(cams);
      setMicrophones(mics);
      setSpeakers(spks);

      if (cams.length > 0 && !selectedCamera) setSelectedCamera(getPreferredDeviceId("camera", cams.map((d) => d.deviceId)));
      if (mics.length > 0 && !selectedMic) setSelectedMic(getPreferredDeviceId("microphone", mics.map((d) => d.deviceId)));
      if (spks.length > 0 && !selectedSpeaker) setSelectedSpeaker(getPreferredDeviceId("speaker", spks.map((d) => d.deviceId)));

      return { cams, mics, spks };
    } catch (err) {
      const msg = err instanceof DOMException && err.name === "NotAllowedError"
        ? t("techCheck.permissionDenied")
        : t("techCheck.enumerateError");
      setCameraResult({ status: "error", message: msg });
      setMicResult({ status: "error", message: msg });
      return null;
    }
  }, [selectedCamera, selectedMic, selectedSpeaker, t]);

  const checkCamera = useCallback(async (deviceId?: string) => {
    setCameraResult({ status: "checking", message: t("techCheck.checkingCamera") });

    // Stop previous video
    if (streamRef.current) {
      streamRef.current.getVideoTracks().forEach((t) => t.stop());
    }
    if (videoRef.current) videoRef.current.srcObject = null;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: deviceId ? { deviceId: { exact: deviceId } } : true,
        audio: false,
      });

      if (streamRef.current) {
        stream.getVideoTracks().forEach((t) => streamRef.current!.addTrack(t));
      } else {
        streamRef.current = stream;
      }

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }

      const settings = stream.getVideoTracks()[0]?.getSettings();
      const label = stream.getVideoTracks()[0]?.label || t("techCheck.unknownCamera");
      const resolution = settings?.width && settings?.height
        ? `${settings.width}x${settings.height}`
        : "";

      setCameraResult({
        status: "ok",
        message: resolution ? `${t("techCheck.cameraOk")} (${resolution})` : t("techCheck.cameraOk"),
        deviceLabel: label,
      });
    } catch (err) {
      setCameraResult({
        status: "error",
        message: err instanceof DOMException && err.name === "NotAllowedError"
          ? t("techCheck.cameraPermissionDenied")
          : t("techCheck.cameraError"),
      });
    }
  }, [t]);

  const checkMicrophone = useCallback(async (deviceId?: string) => {
    setMicResult({ status: "checking", message: t("techCheck.checkingMic") });

    if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: deviceId ? { deviceId: { exact: deviceId } } : true,
        video: false,
      });

      const audioContext = new AudioContext();
      audioContextRef.current = audioContext;
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);

      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      const updateLevel = () => {
        analyser.getByteFrequencyData(dataArray);
        const avg = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
        setAudioLevel(Math.min(100, Math.round((avg / 128) * 100)));
        animFrameRef.current = requestAnimationFrame(updateLevel);
      };
      updateLevel();

      const label = stream.getAudioTracks()[0]?.label || t("techCheck.unknownMic");
      setMicResult({
        status: "ok",
        message: t("techCheck.micOk"),
        deviceLabel: label,
      });
    } catch (err) {
      setMicResult({
        status: "error",
        message: err instanceof DOMException && err.name === "NotAllowedError"
          ? t("techCheck.micPermissionDenied")
          : t("techCheck.micError"),
      });
    }
  }, [t]);

  const checkSpeaker = useCallback(async () => {
    setSpeakerResult({ status: "checking", message: t("techCheck.checkingSpeaker") });

    try {
      const ctx = new AudioContext();
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();
      oscillator.type = "sine";
      oscillator.frequency.setValueAtTime(440, ctx.currentTime);
      gainNode.gain.setValueAtTime(0.3, ctx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.5);
      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);
      oscillator.start();
      oscillator.stop(ctx.currentTime + 0.5);
      setTimeout(() => ctx.close(), 600);

      setSpeakerResult({
        status: "ok",
        message: t("techCheck.speakerOk"),
      });
    } catch {
      setSpeakerResult({
        status: "error",
        message: t("techCheck.speakerError"),
      });
    }
  }, [t]);

  const runAllChecks = useCallback(async () => {
    setCameraResult({ status: "checking", message: t("techCheck.checking") });
    setMicResult({ status: "checking", message: t("techCheck.checking") });
    setSpeakerResult({ status: "checking", message: t("techCheck.checking") });
    setAudioLevel(0);
    stopStream();

    const devices = await enumerateDevices();
    if (!devices) return;

    await checkCamera(selectedCamera || undefined);
    await checkMicrophone(selectedMic || undefined);
    await checkSpeaker();
  }, [enumerateDevices, checkCamera, checkMicrophone, checkSpeaker, selectedCamera, selectedMic, stopStream, t]);

  // Auto-run when modal opens
  useEffect(() => {
    if (open) {
      runAllChecks();
    }
  }, [open, runAllChecks]);

  const allOk = cameraResult.status === "ok" && micResult.status === "ok";

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>{t("techCheck.title")}</span>
            <Button variant="outline" size="sm" onClick={runAllChecks} className="gap-2">
              <RefreshCw className="h-3.5 w-3.5" />
              {t("techCheck.recheck")}
            </Button>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5 mt-2">
          {/* Overall status */}
          {allOk && (
            <div className="rounded-lg bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 p-3 flex items-center gap-3">
              <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0" />
              <p className="text-sm font-medium text-green-800 dark:text-green-200">
                {t("techCheck.allOk")}
              </p>
            </div>
          )}

          {/* Camera */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {cameraResult.status === "ok" ? (
                  <Camera className="h-4 w-4 text-green-600" />
                ) : cameraResult.status === "error" ? (
                  <CameraOff className="h-4 w-4 text-red-600" />
                ) : (
                  <Camera className="h-4 w-4 text-muted-foreground" />
                )}
                <span className="text-sm font-medium">{t("settings.camera")}</span>
                {cameraResult.deviceLabel && (
                  <span className="text-xs text-muted-foreground">- {cameraResult.deviceLabel}</span>
                )}
              </div>
              <StatusBadge status={cameraResult.status} />
            </div>

            <div className="relative aspect-video bg-stone-900 rounded-lg overflow-hidden max-h-48">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="w-full h-full object-cover"
              />
              {cameraResult.status !== "ok" && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center text-white/70">
                    {cameraResult.status === "checking" ? (
                      <RefreshCw className="h-6 w-6 mx-auto mb-2 animate-spin" />
                    ) : (
                      <CameraOff className="h-6 w-6 mx-auto mb-2" />
                    )}
                    <p className="text-xs">{cameraResult.message}</p>
                  </div>
                </div>
              )}
            </div>

            {cameras.length > 1 && (
              <DeviceSelector
                devices={cameras}
                selected={selectedCamera}
                onChange={(id) => {
                  setSelectedCamera(id);
                  saveDevicePreference("camera", id);
                  checkCamera(id);
                }}
              />
            )}
          </div>

          {/* Microphone */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {micResult.status === "ok" ? (
                  <Mic className="h-4 w-4 text-green-600" />
                ) : micResult.status === "error" ? (
                  <MicOff className="h-4 w-4 text-red-600" />
                ) : (
                  <Mic className="h-4 w-4 text-muted-foreground" />
                )}
                <span className="text-sm font-medium">{t("settings.microphone")}</span>
                {micResult.deviceLabel && (
                  <span className="text-xs text-muted-foreground">- {micResult.deviceLabel}</span>
                )}
              </div>
              <StatusBadge status={micResult.status} />
            </div>

            {/* Audio level meter */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>{t("techCheck.level")}</span>
                <span className="font-mono">{audioLevel}%</span>
              </div>
              <div className="h-3 bg-stone-200 dark:bg-stone-800 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-75 ${
                    audioLevel > 60 ? "bg-green-500" : audioLevel > 20 ? "bg-yellow-500" : "bg-stone-400"
                  }`}
                  style={{ width: `${audioLevel}%` }}
                />
              </div>
              <p className="text-xs text-muted-foreground">{t("techCheck.speakToTest")}</p>
            </div>

            {microphones.length > 1 && (
              <DeviceSelector
                devices={microphones}
                selected={selectedMic}
                onChange={(id) => {
                  setSelectedMic(id);
                  saveDevicePreference("microphone", id);
                  checkMicrophone(id);
                }}
              />
            )}
          </div>

          {/* Speaker */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Volume2 className={`h-4 w-4 ${
                  speakerResult.status === "ok" ? "text-green-600" : "text-muted-foreground"
                }`} />
                <span className="text-sm font-medium">{t("techCheck.speaker")}</span>
              </div>
              <StatusBadge status={speakerResult.status} />
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={checkSpeaker}
              className="w-full gap-2"
            >
              <Volume2 className="h-4 w-4" />
              {t("techCheck.playTestTone")}
            </Button>

            {speakers.length > 1 && (
              <DeviceSelector
                devices={speakers}
                selected={selectedSpeaker}
                onChange={(id) => {
                  setSelectedSpeaker(id);
                  saveDevicePreference("speaker", id);
                  checkSpeaker();
                }}
              />
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function StatusBadge({ status }: { status: DeviceStatus }) {
  switch (status) {
    case "ok":
      return (
        <Badge variant="outline" className="text-green-600 border-green-300 bg-green-50 dark:bg-green-950/30 text-xs">
          <CheckCircle2 className="h-3 w-3 mr-1" /> OK
        </Badge>
      );
    case "error":
      return (
        <Badge variant="outline" className="text-red-600 border-red-300 bg-red-50 dark:bg-red-950/30 text-xs">
          <XCircle className="h-3 w-3 mr-1" /> Fehler
        </Badge>
      );
    case "checking":
      return (
        <Badge variant="outline" className="text-blue-600 border-blue-300 bg-blue-50 dark:bg-blue-950/30 text-xs">
          <RefreshCw className="h-3 w-3 mr-1 animate-spin" /> ...
        </Badge>
      );
    default:
      return (
        <Badge variant="outline" className="text-muted-foreground text-xs">
          -
        </Badge>
      );
  }
}

function DeviceSelector({
  devices,
  selected,
  onChange,
}: {
  devices: MediaDeviceInfo[];
  selected: string;
  onChange: (deviceId: string) => void;
}) {
  return (
    <div className="relative">
      <select
        value={selected}
        onChange={(e) => onChange(e.target.value)}
        className="w-full appearance-none rounded-md border border-input bg-background px-3 py-1.5 pr-8 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
      >
        {devices.map((d, i) => (
          <option key={d.deviceId || i} value={d.deviceId}>
            {d.label || `Gerät ${i + 1}`}
          </option>
        ))}
      </select>
      <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
    </div>
  );
}
